import {CurrentUser} from "../../../services/user/models/CurrentUser";
import {
    DashboardConfiguration,
    DashboardLayout,
    DashboardViewConfiguration
} from "../models/DashboardViewConfiguration";
import {WidgetType} from "../models/WidgetType";
import {SecurityIndicesService} from "../../../security/indices/service/SecurityIndicesService";
import {WatchListStore} from "../../../watchlist/store/WatchListStore";
import {WatchlistWidgetConfig} from "../components/widgets/watchlist/models/WatchlistWidgetConfig";
import {Observable} from "rxjs";
import {Exchange} from "../../../instruments/exchange/Exchange";
import {Securities} from "../../../instruments/types/CodeAndExchange";
import {Branding} from "../../../types/Branding";
import {IFilterService} from "angular";

export class DefaultDashboardConfigurationService {

    public static readonly id = "defaultDashboardConfigurationService";
    private readonly translateFilter: (value: string) => string;

    /** @ngInject */
    public constructor(private readonly securityIndicesService: SecurityIndicesService,
                       private readonly watchListStore: WatchListStore,
                       $filter: IFilterService) {
        this.translateFilter = $filter("translate");
    }

    public async forUser(user: CurrentUser): Promise<DashboardViewConfiguration> {
        if (user.isClient()) {
            const config = await this.forClient(user);
            return {
                1: config
            };
        } else {
            const config = await this.defaultAdviserDashboard(user);
            return {
                1: config
            };
        }
    }

    private async forClient(user: CurrentUser): Promise<DashboardConfiguration> {
        switch (user.branding()) {
            case Branding.OPEN_TRADER:
                return await this.defaultOpenTraderClientDashboard(user);
            default:
                return await this.defaultClientDashboard(user);
        }
    }
    
    private async defaultAdviserDashboard(currentUser: CurrentUser): Promise<DashboardConfiguration> {
        const watchlistConfig = await this.watchListWidgetWithAsx20();
        const name = this.translateFilter("dashboard.HOME");
        return {
            name,
            layout: {
                security: null,
                type: DashboardLayout.TWO_BY_TWO,
                widgets: {
                    1: {
                        linked: true,
                        type: WidgetType.WATCHLIST,
                        config: watchlistConfig
                    },
                    2: {
                        linked: true,
                        type: WidgetType.CHART
                    },
                    4: {
                        linked: true,
                        type: WidgetType.ORDER_DEPTH
                    }
                }
            }
        };
    }

    private async defaultClientDashboard(currentUser: CurrentUser): Promise<DashboardConfiguration> {
        const watchlistConfig = await this.watchListWidgetWithAsx20();
        const name = this.translateFilter("dashboard.HOME");
        return {
            name,
            layout: {
                security: null,
                type: DashboardLayout.TWO_BY_TWO,
                widgets: {
                    1: {
                        linked: true,
                        type: WidgetType.WATCHLIST,
                        config: watchlistConfig
                    },
                    2: {
                        linked: true,
                        type: WidgetType.CHART
                    },
                    4: {
                        linked: true,
                        type: WidgetType.ORDER_DEPTH
                    }
                }
            }
        };
    }

    private async defaultOpenTraderClientDashboard(currentUser: CurrentUser): Promise<DashboardConfiguration> {
        const watchlistConfig = await this.watchListWidgetWithAsx20();
        const name = this.translateFilter("dashboard.HOME");
        return {
            name,
            layout: {
                security: null,
                type: DashboardLayout.TWO_BY_TWO,
                widgets: {
                    1: {
                        linked: false,
                        type: WidgetType.OPEN_TRADER_HELP
                    },
                    2: {
                        linked: true,
                        type: WidgetType.WATCHLIST,
                        config: watchlistConfig
                    },
                    3: {
                        linked: true,
                        type: WidgetType.CHART
                    },
                    4: {
                        linked: true,
                        type: WidgetType.ORDER_DEPTH
                    }
                }
            }
        };
    }

    private watchListWidgetWithAsx20(): Promise<WatchlistWidgetConfig> {
        const name = this.translateFilter("dashboard.MY_WATCHLIST");

        const nextWatchlistId = this.watchListStore.newWatchlists.first();

        return Observable.of(name)
                         .flatMap((n) => {
                             setTimeout(() => this.watchListStore.create(n));
                             return nextWatchlistId;
                         })
                         .withLatestFrom(this.watchlistCodes())
                         .map(([id, asx20]) => {
                             const codes = asx20.concat({code: "XJO", exchange: Exchange.ASX});
                             this.watchListStore.addItems(id, codes);
                             return id;
                         })
                         .map((id) => {
                             return {
                                 selected: id
                             };
                         }).toPromise();
    }

    private watchlistCodes(): Observable<Securities> {
        return Observable.fromPromise(this.securityIndicesService.asx20())
                         .catch((err) => {
                             console.error("Unable to retrieve default watchlist codes", err);
                             return Observable.of([]);
                         });
    }
}
